import { useCallback } from 'react';
import { InboxView } from '@weave/schema-gen-ts/dist/schemas/sms/data/v4/sms_service.pb';
import { SMS, Thread, SMSTask } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { Compulsory } from 'ts-toolbelt/out/Object/Compulsory';
import { getUser } from '@frontend/auth-helpers';
import { Options } from '@frontend/fetch';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { useSchemaQueryUpdaters } from '@frontend/react-query-helpers';
import { CountThreadsStatusUpdate } from '../../types';
import { useFetchGetSMS } from '../../v3/queries';
import { useFetchGetThreadDetails } from '../queries';
import { serviceName } from '../service';
import { ServiceQueries } from '../types';
import {
  listThreadsThreadUpdater,
  orderListThreadsThreadUpdatesByUpdateType,
  updateListThreadsData,
} from './endpoint-updater-functions';
import { EndpointQueryData, ResourceUpdateOptions, ResourceUpdateResult } from './types';
import {
  getAffectedCountThreadsPredicate,
  getCountModifier,
  matchThreadToSms,
  threadCouldBeInListThreadsResponse,
  upsertSmsOntoGetThreadQuery,
  upsertSmsOntoThread,
  useGetThreadDetails,
} from './utils';

type UseSMSDataV4QueryUpdatersOptions = {
  httpOptions?: Options;
};

/**
 * This hook is intended to be used by react-query mutation hooks internally in api libraries.
 * Other use cases should use the corresponding mutations instead.
 */
export const useSMSDataV4QueryUpdaters = ({ httpOptions }: UseSMSDataV4QueryUpdatersOptions = {}) => {
  const queryUpdaters = useSchemaQueryUpdaters<ServiceQueries>(serviceName);
  const getThreadDetails = useGetThreadDetails({ httpOptions });
  const fetchThreadDetails = useFetchGetThreadDetails();
  const fetchGetSMS = useFetchGetSMS();
  const user = getUser();

  const updateThreadCounts = useCallback(
    async ({
      threadIds,
      locationId,
      newStatus,
      isOptimistic = false,
    }: {
      threadIds: string[];
      locationId: string;
      newStatus: CountThreadsStatusUpdate;
      isOptimistic?: boolean;
    }) => {
      const originalThreads = await Promise.allSettled(
        threadIds.map(async (threadId) => {
          const thread = await getThreadDetails({
            threadId,
            locationId,
            excludeLastAutomated: false,
            cacheOnly: true,
          });

          return thread;
        })
      );
      const filteredOriginalThreads = originalThreads.reduce<Thread[]>((acc, result) => {
        if (result.status === 'fulfilled' && result.value) acc.push(result.value);
        return acc;
      }, []);
      const allThreadsAreCached = filteredOriginalThreads.length === threadIds.length;
      if (!allThreadsAreCached) {
        // If not all the threads are cached, we do not know if the update should affect the counts.
        // So, if this is an optimistic update, we do not update the counts.
        // For non-optimistic updates, we refetch the count.
        if (!isOptimistic) {
          queryUpdaters.invalidateQueries({
            endpointName: 'CountThreads',
            queryFilters: {
              predicate: getAffectedCountThreadsPredicate({ newStatus, locationId }),
            },
          });
        }

        return {
          revertCacheUpdate: () => {},
        };
      }

      const originalQueries = queryUpdaters.getQueriesData<'CountThreads'>({
        endpointName: 'CountThreads',
        queryFilters: {
          predicate: getAffectedCountThreadsPredicate({ newStatus, locationId }),
        },
      });

      originalQueries.forEach(([queryKey]) => {
        const request = queryKey[2];
        queryUpdaters.updateQuery<'CountThreads'>({
          endpointName: 'CountThreads',
          queryFilters: {
            queryKey,
            exact: true,
          },
          updater: (oldData) => {
            const modifier = getCountModifier(request, newStatus);
            const newCount =
              modifier === 'none'
                ? oldData.count
                : oldData.count + (modifier === 'add' ? threadIds.length : -1 * threadIds.length);
            return {
              ...oldData,
              count: Math.max(0, newCount),
            };
          },
        });
      });

      return {
        revertCacheUpdate: () => {
          originalQueries.forEach(([queryKey, data]) => {
            queryUpdaters.updateQuery<'CountThreads'>({
              endpointName: 'CountThreads',
              queryFilters: {
                queryKey,
                exact: true,
              },
              updater: () => data,
            });
          });
        },
      };
    },
    [queryUpdaters, getThreadDetails]
  );

  const upsertThread = useCallback(
    async ({
      matchValues,
      matchFn,
      matchStrategy,
      updater,
      isOptimistic,
    }: ResourceUpdateOptions<Thread>): Promise<ResourceUpdateResult> => {
      const cachedThread = await getThreadDetails({
        threadId: matchValues.threadId,
        locationId: matchValues.locationId,
        cacheOnly: isOptimistic,
        excludeLastAutomated: false,
        matchStrategy,
        matchFn,
      });
      const anyListThreadsIsManualOnly = !!queryUpdaters.getQueriesData({
        endpointName: 'ListThreads',
        queryFilters: {
          queryKey: queryUpdaters.getQueryKey<'ListThreads'>({
            endpointName: 'ListThreads',
            request: {
              filter: {
                excludeAutomatedLastMessage: true,
              },
            },
          }),
          exact: false,
        },
      }).length;
      const cachedManualThread = cachedThread?.messages[0]?.autogeneratedBy
        ? anyListThreadsIsManualOnly
          ? await getThreadDetails({
              threadId: matchValues.threadId,
              locationId: matchValues.locationId,
              cacheOnly: isOptimistic,
              excludeLastAutomated: true,
              matchStrategy,
              matchFn,
            })
          : undefined
        : cachedThread;

      const threadFromUpdater = await updater(cachedThread);
      const manualThreadFromUpdater = await updater(cachedManualThread);
      const resolvedManualThreadFromUpdater = manualThreadFromUpdater?.messages[0]?.autogeneratedBy
        ? undefined
        : manualThreadFromUpdater;

      // Ensure there are no discrepancies between the updatedThread and the message on it
      const updatedThread = threadFromUpdater
        ? matchThreadToSms({
            thread: threadFromUpdater,
            originalSms: cachedThread?.messages[0],
            updatedSms: threadFromUpdater?.messages[0],
          })
        : undefined;
      const updatedExcludeAutomatedThread = resolvedManualThreadFromUpdater
        ? matchThreadToSms({
            thread: resolvedManualThreadFromUpdater,
            originalSms: cachedManualThread?.messages[0],
            updatedSms: resolvedManualThreadFromUpdater?.messages[0],
          })
        : undefined;

      // ListThreads
      const originalListThreadQueries: EndpointQueryData<'ListThreads'>[] = [];

      if (!!updatedThread || !!updatedExcludeAutomatedThread) {
        const allListThreadsQueries = queryUpdaters.getQueriesData<'ListThreads', true>({
          endpointName: 'ListThreads',
        });
        const { excludeLastAutomatedQueries, otherQueries } = allListThreadsQueries.reduce<{
          excludeLastAutomatedQueries: typeof allListThreadsQueries;
          otherQueries: typeof allListThreadsQueries;
        }>(
          (acc, curr) => {
            const isExcludingLastAutomated = !!curr[0][2]?.filter?.excludeAutomatedLastMessage;
            if (isExcludingLastAutomated) {
              acc.excludeLastAutomatedQueries.push(curr);
            } else {
              acc.otherQueries.push(curr);
            }
            return acc;
          },
          { excludeLastAutomatedQueries: [], otherQueries: [] }
        );

        // Skip queries that exclude automated last messages if new thead is automated
        if (!!updatedExcludeAutomatedThread) {
          const orderedUpdates = orderListThreadsThreadUpdatesByUpdateType(excludeLastAutomatedQueries, {
            matchValues,
            matchFn,
            matchStrategy,
            originalResource: cachedManualThread,
            updatedResource: updatedExcludeAutomatedThread,
          });
          orderedUpdates.forEach(({ queryData, updateType }) => {
            if (updateType === 'none') return;
            const filteredTags = queryData[0][2]?.filter?.messageTags ?? [];
            const updatedData = listThreadsThreadUpdater(
              {
                matchValues,
                matchFn,
                matchStrategy,
                originalResource: cachedManualThread,
                updatedResource: filteredTags.length
                  ? {
                      ...updatedExcludeAutomatedThread,
                      uniqueTags: updatedExcludeAutomatedThread.uniqueTags?.filter((tag) =>
                        filteredTags.includes(tag.tagId)
                      ),
                    }
                  : updatedExcludeAutomatedThread,
                queryData,
              },
              updateType
            );

            if (updatedData) {
              originalListThreadQueries.push(queryData);
              queryUpdaters.updateQuery<'ListThreads', true>({
                endpointName: 'ListThreads',
                queryFilters: {
                  queryKey: queryData[0],
                  exact: true,
                },
                updater: () => updatedData,
              });
            }
          });
        }

        if (!!updatedThread) {
          const orderedUpdates = orderListThreadsThreadUpdatesByUpdateType(otherQueries, {
            matchValues,
            matchFn,
            matchStrategy,
            originalResource: cachedThread,
            updatedResource: updatedThread,
          });
          orderedUpdates.forEach(({ queryData, updateType }) => {
            if (updateType === 'none') return;
            const filteredTags = queryData[0][2]?.filter?.messageTags ?? [];
            const updatedData = listThreadsThreadUpdater(
              {
                matchValues,
                matchFn,
                matchStrategy,
                originalResource: cachedThread,
                updatedResource: filteredTags.length
                  ? {
                      ...updatedThread,
                      uniqueTags: updatedThread.uniqueTags?.filter((tag) => filteredTags.includes(tag.tagId)),
                    }
                  : updatedThread,
                queryData,
              },
              updateType
            );

            if (updatedData) {
              originalListThreadQueries.push(queryData);
              queryUpdaters.updateQuery<'ListThreads', true>({
                endpointName: 'ListThreads',
                queryFilters: {
                  queryKey: queryData[0],
                  exact: true,
                },
                updater: () => updatedData,
              });
            }
          });
        }
      }

      // GetThreadDetails

      const originalGetThreadDetailsQueries: EndpointQueryData<'GetThreadDetails'>[] = [];

      // Never update GetThreadDetails if updating optimistically since it's really only used to update the cache
      if (!isOptimistic) {
        const applicableGetThreadDetailsQueries = queryUpdaters.getQueriesData({
          endpointName: 'GetThreadDetails',
          queryFilters: {
            queryKey: queryUpdaters.getQueryKey<'GetThreadDetails'>({
              endpointName: 'GetThreadDetails',
              request: {
                threadId: matchValues.threadId,
                locationId: matchValues.locationId,
              },
            }),
            exact: false,
            predicate: !resolvedManualThreadFromUpdater
              ? ({ queryKey }) => !queryKey[2]?.excludeAutomatedLastMessage
              : undefined,
          },
        });

        applicableGetThreadDetailsQueries.forEach((queryData) => {
          const excludeLastAutomated = queryData[0][2]?.excludeAutomatedLastMessage;
          if (excludeLastAutomated && updatedExcludeAutomatedThread) {
            originalGetThreadDetailsQueries.push(queryData);
            queryUpdaters.updateQuery({
              endpointName: 'GetThreadDetails',
              queryFilters: {
                queryKey: queryData[0],
              },
              updater: () => ({ thread: updatedExcludeAutomatedThread }),
            });
          } else if (!excludeLastAutomated && updatedThread) {
            originalGetThreadDetailsQueries.push(queryData);
            queryUpdaters.updateQuery({
              endpointName: 'GetThreadDetails',
              queryFilters: {
                queryKey: queryData[0],
              },
              updater: () => ({ thread: updatedThread }),
            });
          }
        });
      }

      // TODO: GetThread

      return {
        revertCacheUpdate: () => {
          originalListThreadQueries.forEach(([queryKey, queryData]) => {
            queryUpdaters.updateQuery<'ListThreads', true>({
              endpointName: 'ListThreads',
              queryFilters: { queryKey, exact: true },
              updater: () => queryData,
            });
          });
          originalGetThreadDetailsQueries.forEach(([queryKey, queryData]) => {
            queryUpdaters.updateQuery({
              endpointName: 'GetThreadDetails',
              queryFilters: { queryKey, exact: true },
              updater: () => queryData,
            });
          });
        },
      };
    },
    [getThreadDetails, queryUpdaters]
  );

  const upsertSms = useCallback(
    async ({
      matchValues,
      updater,
      isOptimistic,
      getThreadOnly,
      updateOnly,
    }: ResourceUpdateOptions<SMS> & {
      getThreadOnly?: boolean;
      updateOnly?: boolean;
    }): Promise<ResourceUpdateResult> => {
      let upsertThreadResult: ResourceUpdateResult | undefined = undefined;
      if (!getThreadOnly) {
        upsertThreadResult = await upsertThread({
          matchValues: {
            threadId: matchValues.threadId,
            locationId: matchValues.locationId,
          },
          updater: async (thread) => {
            if (!thread) {
              return undefined;
            }

            const matchingSms = thread.messages[0]?.id === matchValues.smsId ? thread.messages[0] : undefined;

            const updatedSms = await updater(matchingSms);

            if (!updatedSms) return undefined;
            return upsertSmsOntoThread(thread, updatedSms);
          },
          isOptimistic,
        });
      }

      // handle GetThread
      const allApplicableGetThreadQueries = queryUpdaters.getQueriesData<'GetThread', true>({
        endpointName: 'GetThread',
        queryFilters: {
          queryKey: queryUpdaters.getQueryKey<'GetThread'>({
            endpointName: 'GetThread',
            request: {
              threadId: matchValues.threadId,
              locationId: matchValues.locationId,
            },
          }),
          exact: false,
        },
      });
      let cachedSms: SMS | undefined = undefined;
      for (const query of allApplicableGetThreadQueries) {
        if (cachedSms) break;
        const queryData = query[1];
        for (const page of queryData.pages) {
          if (cachedSms) break;
          for (const sms of page.thread.messages) {
            if (cachedSms) break;
            if (sms.id === matchValues.smsId) {
              cachedSms = sms;
              break;
            }
          }
        }
      }

      let updatedSms = await updater(cachedSms);
      if (!updatedSms && !updateOnly) {
        let fetchedSms: SMS | undefined;
        try {
          const start = matchValues.createdAt
            ? new Date(new Date(matchValues.createdAt).getTime() - 1000).toISOString()
            : undefined;
          const end = matchValues.createdAt
            ? new Date(new Date(matchValues.createdAt).getTime() + 1000).toISOString()
            : undefined;
          const fetchedSmsResponse = await fetchGetSMS({
            request: {
              smsId: matchValues.smsId,
              locationId: matchValues.locationId,
              tagsEnabled: true,
              start,
              end,
            },
            httpOptions,
          });
          fetchedSms = fetchedSmsResponse.sms;
        } catch {
          fetchedSms = undefined;
        }

        updatedSms = isOptimistic ? await updater(fetchedSms) : fetchedSms;
      }

      if (updatedSms) {
        allApplicableGetThreadQueries.forEach(([queryKey]) => {
          queryUpdaters.updateQuery<'GetThread', true>({
            endpointName: 'GetThread',
            queryFilters: {
              queryKey,
              exact: true,
            },
            updater: (oldData) => {
              if (!oldData) return oldData;

              const [, newData] = upsertSmsOntoGetThreadQuery({
                query: [queryKey, oldData],
                newSms: updatedSms,
                matchingSmsId: matchValues.smsId,
              });
              return newData;
            },
          });
        });
      }

      return {
        revertCacheUpdate: () => {
          upsertThreadResult?.revertCacheUpdate();
          if (updatedSms) {
            allApplicableGetThreadQueries.forEach(([queryKey, originalData]) => {
              queryUpdaters.updateQuery<'GetThread', true>({
                endpointName: 'GetThread',
                queryFilters: { queryKey, exact: true },
                updater: () => originalData,
              });
            });
          }
        },
      };
    },
    [upsertThread, queryUpdaters, fetchGetSMS, httpOptions]
  );

  const deleteSms = useCallback(
    async ({
      matchValues,
      deletedAt = new Date().toISOString(),
      deletedBy = user?.userID ?? '',
      isOptimistic,
    }: Omit<ResourceUpdateOptions<SMS>, 'updater' | 'isAutomated' | 'matchFn' | 'matchStrategy'> & {
      deletedAt?: string;
      deletedBy?: string;
    }) => {
      let updatedThread: Thread | undefined;

      if (!isOptimistic) {
        queryUpdaters.invalidateQueries({
          endpointName: 'GetThreadDetails',
          queryFilters: {
            queryKey: queryUpdaters.getQueryKey<'GetThreadDetails'>({
              endpointName: 'GetThreadDetails',
              request: {
                threadId: matchValues.threadId,
                locationId: matchValues.locationId,
              },
            }),
            exact: false,
          },
        });
        try {
          const response = await fetchThreadDetails({
            request: {
              threadId: matchValues.threadId,
              locationId: matchValues.locationId,
            },
            httpOptions,
          });
          if (response.thread.id) {
            updatedThread = response.thread;
          }
        } catch (error) {
          console.error('Error fetching thread details:', error);
        }
      }

      let anyQueryExcludesAutomated = false;
      const queriesToUpdate = queryUpdaters.getQueriesData<'ListThreads', true>({
        endpointName: 'ListThreads',
        queryFilters: {
          exact: false,
          predicate: ({ queryKey, state: { data } }) => {
            const request = queryKey[2];
            const matchesLocations = request.locationIds?.includes(matchValues.locationId);
            if (!matchesLocations) return false;

            if (updatedThread && request.locationIds?.length) {
              const typedRequest = request as Compulsory<typeof request, 'locationIds'>;
              const updatedThreadShouldBeInQuery = threadCouldBeInListThreadsResponse({
                thread: updatedThread,
                listThreadsRequest: typedRequest,
              });
              if (updatedThreadShouldBeInQuery) return true;
            }

            const hasThreadWithSms = !!data?.pages.some((page) =>
              page.threads.some((thread) => {
                const sms = thread.messages[0];
                if (!sms) return false;
                const isMatchingThread = thread.id === matchValues.threadId && sms?.id === matchValues.smsId;
                if (!updatedThread && isOptimistic)
                  updatedThread = {
                    ...thread,
                    messages: [
                      {
                        ...sms,
                        deletedAt,
                        deletedBy,
                        tags: [],
                        tagsDetailed: [],
                        tasks: [],
                      },
                    ],
                    // TODO: update tasks and uniqueTags for optimistic flow,
                  };
                return isMatchingThread;
              })
            );
            if (hasThreadWithSms && request.filter?.excludeAutomatedLastMessage) {
              anyQueryExcludesAutomated = true;
            }
            return hasThreadWithSms;
          },
        },
      });
      if (!queriesToUpdate.length) {
        return {
          revertCacheUpdate: () => {},
        };
      }

      if (!updatedThread) {
        return {
          revertCacheUpdate: () => {},
        };
      }
      let updatedThreadWithExcludedAutomated: Thread | undefined = updatedThread.messages[0]?.autogeneratedBy
        ? undefined
        : updatedThread;
      if (anyQueryExcludesAutomated && !updatedThreadWithExcludedAutomated) {
        const response = await fetchThreadDetails({
          request: {
            threadId: matchValues.threadId,
            locationId: matchValues.locationId,
            excludeAutomatedLastMessage: true,
          },
          httpOptions,
        });
        updatedThreadWithExcludedAutomated = response.thread;
      }

      queriesToUpdate.forEach(([queryKey]) => {
        queryUpdaters.updateQuery<'ListThreads', true>({
          endpointName: 'ListThreads',
          queryFilters: {
            queryKey,
            exact: true,
          },
          updater: (oldData, queryKey) => {
            const request = queryKey[2];
            const excludeLastAutomated = !!request.filter?.excludeAutomatedLastMessage;

            if (!isOptimistic) {
              const threadToUse = excludeLastAutomated ? updatedThreadWithExcludedAutomated : updatedThread;
              if (!threadToUse) return oldData;

              return (
                updateListThreadsData({
                  matchValues: {
                    threadId: matchValues.threadId,
                    locationId: matchValues.locationId,
                  },
                  queryData: [queryKey, oldData],
                  updatedResource: threadToUse,
                  inPlace: false,
                }) ?? oldData
              );
            } else {
              return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                  ...page,
                  threads: page.threads.map((thread) =>
                    thread.id === matchValues.threadId
                      ? {
                          ...thread,
                          messages: thread.messages.map((sms) =>
                            sms.id === matchValues.smsId
                              ? {
                                  ...sms,
                                  deletedAt,
                                  deletedBy,
                                  tags: [],
                                  tagsDetailed: [],
                                  tasks: [],
                                }
                              : sms
                          ),
                          // TODO: update tasks and uniqueTags for optimistic flow,
                        }
                      : thread
                  ),
                })),
              };
            }
          },
        });
      });

      upsertSms({
        matchValues: {
          smsId: matchValues.smsId,
          threadId: matchValues.threadId,
          locationId: matchValues.locationId,
        },
        updateOnly: true,
        isOptimistic,
        getThreadOnly: true,
        updater: (sms) =>
          sms
            ? {
                ...sms,
                tags: [],
                tagsDetailed: [],
                tasks: [],
                deletedAt,
                deletedBy,
              }
            : undefined,
      });

      // TODO: implement CountThreads

      return {
        revertCacheUpdate: () => {
          queriesToUpdate.forEach(([queryKey, originalData]) => {
            queryUpdaters.updateQuery<'ListThreads', true>({
              endpointName: 'ListThreads',
              queryFilters: { queryKey, exact: true },
              updater: () => originalData,
            });
          });
        },
      };
    },
    [fetchThreadDetails, httpOptions, queryUpdaters, upsertSms, user?.userID]
  );

  const togglePersonPhoneBlock = useCallback(
    ({
      personPhone,
      isBlocked,
      locationId,
      isOptimistic,
    }: {
      personPhone: string;
      isBlocked: boolean;
      locationId: string;
      isOptimistic: boolean;
    }) => {
      // ListThreads
      const allListThreadsQueries = queryUpdaters.getQueriesData<'ListThreads', true>({
        endpointName: 'ListThreads',
        queryFilters: {
          predicate: ({ queryKey }) => {
            const request = queryKey[2];
            const locationIdMatches = !!request.locationIds?.includes(locationId);

            if (!locationIdMatches) return false;
            if (
              request.filter?.personPhones?.length &&
              !request.filter.personPhones.some(
                (filterPersonPhone) => formatPhoneNumberE164(filterPersonPhone) === formatPhoneNumberE164(personPhone)
              )
            ) {
              return false;
            }
            return true;
          },
        },
      });

      const { queriesToFilter, queriesToInvalidate } = allListThreadsQueries.reduce<{
        queriesToFilter: typeof allListThreadsQueries;
        queriesToInvalidate: typeof allListThreadsQueries;
      }>(
        (acc, curr) => {
          const isBlockedInboxView = (curr[0][2].filter?.inboxView ?? InboxView.UNSPECIFIED) === InboxView.BLOCKED;
          const viewCanBeFiltered = isBlockedInboxView !== isBlocked;

          if (viewCanBeFiltered) {
            acc.queriesToFilter.push(curr);
          } else {
            acc.queriesToInvalidate.push(curr);
          }

          return acc;
        },
        { queriesToFilter: [], queriesToInvalidate: [] }
      );

      queriesToFilter.forEach(([queryKey]) => {
        queryUpdaters.updateQuery<'ListThreads', true>({
          endpointName: 'ListThreads',
          queryFilters: {
            queryKey,
            exact: true,
          },
          updater: (oldData) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                threads: page.threads.filter((thread) => {
                  if (thread.locationId !== locationId) return true;
                  const threadPersonPhone = thread.messages[0]?.personPhone;
                  if (!threadPersonPhone) return true;

                  return formatPhoneNumberE164(threadPersonPhone) !== formatPhoneNumberE164(personPhone);
                }),
              })),
            };
          },
        });
      });

      if (!isOptimistic) {
        queriesToInvalidate.forEach(([queryKey]) => {
          queryUpdaters.invalidateQueries({
            endpointName: 'ListThreads',
            queryFilters: {
              queryKey,
              exact: true,
            },
          });
        });
      }

      const originalGetThreadDetailsQueries: EndpointQueryData<'GetThreadDetails'>[] = [];
      queryUpdaters.updateQuery({
        endpointName: 'GetThreadDetails',
        queryFilters: {
          queryKey: queryUpdaters.getQueryKey<'GetThreadDetails'>({
            endpointName: 'GetThreadDetails',
            request: {
              locationId,
            },
          }),
          exact: false,
          predicate: ({ queryKey, state: { data } }) => {
            const personPhoneMatches = !!data?.thread.messages.some(
              (sms) => formatPhoneNumberE164(sms.personPhone) === formatPhoneNumberE164(personPhone)
            );
            if (personPhoneMatches && !!data) {
              originalGetThreadDetailsQueries.push([queryKey, data]);
            }
            return personPhoneMatches;
          },
        },
        updater: (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            thread: {
              ...oldData.thread,
              isBlocked,
            },
          };
        },
      });

      const originalGetThreadQueries: EndpointQueryData<'GetThread'>[] = [];
      queryUpdaters.updateQuery<'GetThread', true>({
        endpointName: 'GetThread',
        queryFilters: {
          queryKey: queryUpdaters.getQueryKey<'GetThread'>({
            endpointName: 'GetThread',
            request: {
              locationId,
            },
          }),
          exact: false,
          predicate: ({ queryKey, state: { data } }) => {
            const personPhoneMatches = !!data?.pages.some(
              (page) =>
                !!page.thread.messages.some(
                  (sms) => formatPhoneNumberE164(sms.personPhone) === formatPhoneNumberE164(personPhone)
                )
            );
            if (personPhoneMatches && !!data) {
              originalGetThreadQueries.push([queryKey, data]);
            }
            return personPhoneMatches;
          },
        },
        updater: (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => {
              return {
                ...page,
                thread: {
                  ...page.thread,
                  isBlocked,
                },
              };
            }),
          };
        },
      });

      if (!isOptimistic) {
        // Wait for BE to be ready to have a new count after blocking/unblocking
        setTimeout(() => {
          queryUpdaters.invalidateQueries({
            endpointName: 'CountThreads',
          });
        }, 500);
      }

      return {
        revertCacheUpdate: () => {
          queriesToFilter.forEach(([queryKey, queryData]) => {
            queryUpdaters.updateQuery<'ListThreads', true>({
              endpointName: 'ListThreads',
              queryFilters: { queryKey, exact: true },
              updater: () => queryData,
            });
          });
          originalGetThreadDetailsQueries.forEach(([queryKey, originalData]) => {
            queryUpdaters.updateQuery<'GetThreadDetails'>({
              endpointName: 'GetThreadDetails',
              queryFilters: { queryKey, exact: true },
              updater: () => originalData,
            });
          });
          originalGetThreadQueries.forEach(([queryKey, originalData]) => {
            queryUpdaters.updateQuery<'GetThread', true>({
              endpointName: 'GetThread',
              queryFilters: { queryKey, exact: true },
              updater: () => originalData,
            });
          });
        },
      };
    },
    [queryUpdaters]
  );

  const updateThreadsWithNewPerson = useCallback(
    ({
      personPhones,
      personId,
      firstName,
      lastName,
      preferredName,
      onlyEmptyPersons,
      locationId,
    }: {
      personPhones: string[];
      personId: string;
      firstName: string;
      lastName: string;
      preferredName: string;
      onlyEmptyPersons: boolean;
      locationId: string;
    }) => {
      const formattedPersonPhones = personPhones.map(formatPhoneNumberE164);

      queryUpdaters.updateQuery<'ListThreads', true>({
        endpointName: 'ListThreads',
        updater: (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              threads: page.threads.map((thread) => {
                if (thread.locationId !== locationId) return thread;
                if (!!thread.person?.personId && onlyEmptyPersons) return thread;

                const threadPhone = thread.messages[0]?.personPhone;
                if (!threadPhone) return thread;

                const formattedThreadPhone = formatPhoneNumberE164(threadPhone);
                if (formattedPersonPhones.includes(formattedThreadPhone)) {
                  return {
                    ...thread,
                    person: {
                      personId,
                      firstName,
                      lastName,
                      preferredName,
                    },
                    messages: thread.messages.map((sms) => {
                      if (sms.personId && onlyEmptyPersons) return sms;

                      if (formattedPersonPhones.includes(formatPhoneNumberE164(sms.personPhone))) {
                        return {
                          ...sms,
                          personId,
                        };
                      }

                      return sms;
                    }),
                  } satisfies typeof thread;
                }

                return thread;
              }),
            })),
          };
        },
      });

      queryUpdaters.updateQuery({
        endpointName: 'GetThreadDetails',
        updater: (oldData) => {
          if (!oldData) return oldData;
          if (oldData.thread.locationId !== locationId) return oldData;
          if (oldData.thread.person?.personId && onlyEmptyPersons) return oldData;

          const threadPhone = oldData.thread.messages[0]?.personPhone;
          if (!threadPhone) return oldData;

          const formattedThreadPhone = formatPhoneNumberE164(threadPhone);
          if (formattedPersonPhones.includes(formattedThreadPhone)) {
            return {
              ...oldData,
              thread: {
                ...oldData.thread,
                person: {
                  personId,
                  firstName,
                  lastName,
                  preferredName,
                },
              },
            };
          }

          return oldData;
        },
      });
    },
    [queryUpdaters]
  );

  const invalidateAllListThreads = useCallback(() => {
    queryUpdaters.invalidateQueries<'ListThreads', true>({
      endpointName: 'ListThreads',
    });
  }, [queryUpdaters]);

  const updateThreadsTasksList = useCallback(
    ({
      threadId,
      locationId,
      task,
      action,
    }: {
      threadId: string;
      locationId: string;
      task: SMSTask;
      action: 'UPDATE' | 'DELETE';
      isOptimistic?: boolean;
    }) => {
      const updateTasksArray = (existingTasks: SMSTask[] = [], newTask: SMSTask, updateAction: 'UPDATE' | 'DELETE') => {
        if (updateAction === 'DELETE') {
          return existingTasks.filter((existingTask) => existingTask.taskId !== newTask.taskId);
        }

        const existingTaskIndex = existingTasks.findIndex((existingTask) => existingTask.taskId === newTask.taskId);
        if (existingTaskIndex >= 0) {
          return existingTasks.map((existingTask) => (existingTask.taskId === newTask.taskId ? newTask : existingTask));
        } else {
          return [...existingTasks, newTask];
        }
      };

      // Helper function to check if thread should be visible based on task status filter
      const shouldThreadBeVisibleForTaskStatus = (
        updatedThread: { tasks?: SMSTask[] },
        taskStatusFilter?: string[]
      ): boolean => {
        if (!taskStatusFilter || taskStatusFilter.length === 0) {
          return true; // No task status filter applied
        }

        const threadTasks = updatedThread.tasks || [];
        if (threadTasks.length === 0) {
          return false; // Thread has no tasks, shouldn't be visible when task filter is applied
        }

        // Check if any of the thread's tasks match the filtered task statuses
        return threadTasks.some((threadTask) => taskStatusFilter.includes(threadTask.status));
      };

      // Get all ListThreads queries that might be affected
      const listThreadsQueries = queryUpdaters.getQueriesData<'ListThreads', true>({
        endpointName: 'ListThreads',
      });

      // Update ListThreads queries with smart filtering
      listThreadsQueries.forEach(([queryKey, data]) => {
        if (!data) return;

        const queryRequest = queryKey[2];
        const taskStatusFilter = queryRequest.filter?.taskStatuses;

        // Check if this query affects our location
        if (!queryRequest.locationIds?.includes(locationId)) {
          return;
        }

        // Find if thread exists in current query data
        const threadExistsInQuery = data.pages.some((page) => page.threads.some((thread) => thread.id === threadId));

        // For DELETE action, also check if the deleted task's status would affect this filter
        const deletedTaskMightAffectThisFilter = action === 'DELETE' && taskStatusFilter?.includes(task.status);

        if (threadExistsInQuery || deletedTaskMightAffectThisFilter) {
          // Thread exists in query OR deleted task might affect this filter, update it
          queryUpdaters.updateQuery<'ListThreads', true>({
            endpointName: 'ListThreads',
            queryFilters: {
              queryKey,
              exact: true,
            },
            updater: (oldData) => {
              if (!oldData) return oldData;

              return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                  ...page,
                  threads: page.threads.reduce<typeof page.threads>((acc, thread) => {
                    if (thread.id === threadId && thread.locationId === locationId) {
                      const updatedThread = {
                        ...thread,
                        tasks: updateTasksArray(thread.tasks, task, action),
                      };

                      // Check if updated thread should still be visible with current filters
                      if (shouldThreadBeVisibleForTaskStatus(updatedThread, taskStatusFilter)) {
                        acc.push(updatedThread);
                      }
                      // If thread should no longer be visible, it's filtered out (not added to acc)
                    } else {
                      acc.push(thread);
                    }
                    return acc;
                  }, []),
                })),
              };
            },
          });
        } else if (action === 'UPDATE') {
          // Thread doesn't exist in current query, but we're updating/adding a task
          // It might now be visible due to the task update
          // Invalidate this query to fetch fresh data
          queryUpdaters.invalidateQueries<'ListThreads', true>({
            endpointName: 'ListThreads',
            queryFilters: {
              queryKey,
              exact: true,
            },
          });
        } else if (action === 'DELETE' && taskStatusFilter?.length) {
          // For DELETE actions on filtered views, invalidate to ensure thread removal
          // This handles cases where thread should be removed from views it's not currently cached in
          queryUpdaters.invalidateQueries<'ListThreads', true>({
            endpointName: 'ListThreads',
            queryFilters: {
              queryKey,
              exact: true,
            },
          });
        }
      });

      // Update GetThreadDetails queries
      queryUpdaters.updateQuery({
        endpointName: 'GetThreadDetails',
        updater: (oldData) => {
          if (!oldData) return oldData;
          if (oldData.thread.id !== threadId || oldData.thread.locationId !== locationId) {
            return oldData;
          }

          return {
            ...oldData,
            thread: {
              ...oldData.thread,
              tasks: updateTasksArray(oldData.thread.tasks, task, action),
            },
          };
        },
      });

      // Update thread counts that might be affected by task status changes
      queryUpdaters.invalidateQueries<'CountThreads'>({
        endpointName: 'CountThreads',
        queryFilters: {
          predicate: ({ queryKey }) => {
            const request = queryKey[2];
            const hasTaskStatusFilter = request.filter?.taskStatuses?.length;
            const matchesLocation = request.locationIds?.includes(locationId);

            // For DELETE actions, invalidate all task-filtered count queries for this location
            // For UPDATE actions, only invalidate if task status filter is present
            if (action === 'DELETE') {
              return !!hasTaskStatusFilter && !!matchesLocation;
            } else {
              return !!hasTaskStatusFilter && !!matchesLocation;
            }
          },
        },
      });
    },
    [queryUpdaters]
  );

  return {
    ...queryUpdaters,
    /**
     * Updates or inserts an SMS message in the query cache.
     * Updates all SMSDataV4 queries that contain this SMS by updating its parent thread.
     * Handles creation date comparison to ensure newer messages are properly updated.
     *
     * @param matchValues - Values to use for matching the SMS
     * @param matchFn - Function to match the SMS to update
     * @param matchStrategy - Strategy for handling the provided match function and values
     * @param updater - Function that takes the existing SMS and returns the updated SMS
     * @param isAutomated - Whether the updated SMS is automated or not
     * @param isOptimistic - Whether this is an optimistic update
     * @param getThreadOnly - Whether to only update the GetThread query
     * @param updateOnly - Whether to only update the SMS (avoid insert)
     * @returns Object containing a function to revert the cache update
     */
    upsertSms,
    /**
     * Marks an SMS message as deleted in the query cache.
     * Updates all SMSDataV4 queries that contain this SMS by updating its parent thread.
     * Handles both automated and non-automated messages appropriately.
     *
     * @param matchValues - Values to use for matching the SMS to delete
     * @param deletedAt - Timestamp for when the SMS was deleted (defaults to current time)
     * @param deletedBy - ID of the user who deleted the SMS (defaults to current user)
     * @param isOptimistic - Whether this is an optimistic update
     * @returns Object containing a function to revert the cache update
     */
    deleteSms,
    /**
     * Updates or inserts a thread in the query cache.
     * Updates all SMSDataV4 queries that contain this thread.
     * Handles both manual and automated messages on the thread appropriately.
     *
     * @param matchValues - Values to use for matching the thread
     * @param matchFn - Function to match the thread to update
     * @param matchStrategy - Strategy for handling the provided match function and values
     * @param updater - Function that takes the existing thread and returns the updated thread
     * @param newThreadIsAutomated - Whether the updated thread's last mesasge is automated or not
     * @param isOptimistic - Whether this is an optimistic update
     * @returns Object containing a function to revert the cache update
     */
    upsertThread,
    /**
     * Toggles the blocked status of messages from a person's phone number.
     * Updates all SMSDataV4 queries that contain affected threads.
     *
     * @param personPhone - Phone number of the person to block/unblock
     * @param isBlocked - Whether the phone number should be blocked
     * @param locationId - ID of the location for which to apply the block
     * @param isOptimistic - Whether this is an optimistic update
     * @returns Object containing a function to revert the cache update
     */
    togglePersonPhoneBlock,
    /**
     * Retrieves details for a specific thread from the cache or API.
     * Can optionally filter out threads with automated last messages.
     *
     * @param threadId - ID of the thread to retrieve
     * @param locationId - ID of the location the thread belongs to
     * @param cacheOnly - Whether to only check the cache (no API calls)
     * @param excludeLastAutomated - Whether to exclude automated messages on the thread object
     * @returns A thread object if found, otherwise `undefined`
     */
    getThreadDetails,
    /**
     * A function to update the thread counts in the cache.
     * This currently only updates the counts for actionable and read status updates and is not comprehensive.
     * Add more status updates as needed.
     *
     * @param threadIds - The threadIds which have been affected by the status update
     * @param locationId - The locationId associated with the threads
     * @param newStatus - The new status to update the counts for: one of 'actionable', 'not-actionable', 'read', or 'unread'
     */
    updateThreadCounts,
    /**
     * Updates all threads and messages in the query cache with new person information for the given phone numbers.
     * Associates the specified person details (personId, firstName, lastName, preferredName) with threads and SMS messages
     * that match any of the provided personPhones. Optionally, only updates threads or messages that do not already have a personId.
     *
     * @param personPhones - List of phone numbers to match
     * @param personId - The ID of the person to associate
     * @param firstName - The first name of the person
     * @param lastName - The last name of the person
     * @param preferredName - The preferred name of the person
     * @param onlyEmptyPersons - If true, only updates threads/messages without an existing personId
     */
    updateThreadsWithNewPerson,
    /**
     * Updates threads with individual task operations in the query cache.
     * Updates all SMSDataV4 queries that contain the specified thread.
     * Utilizes the taskStatuses parameter support available in v4 API.
     * Supports both adding/updating and removing individual tasks.
     * Handles task status filtering and thread visibility changes automatically.
     *
     * @param threadId - The ID of the thread to update
     * @param locationId - The ID of the location the thread belongs to
     * @param task - The individual task to update or delete
     * @param action - The action to perform: 'UPDATE' (add/modify) or 'DELETE' (remove)
     * @param isOptimistic - Whether this is an optimistic update
     */
    updateThreadsTasksList,
    /**
     * Invalidates all ListThreads queries in the cache.
     * This will force all ListThreads queries to refetch from the server on their next use.
     * Useful when you need to ensure fresh data across all thread lists.
     */
    invalidateAllListThreads,
  };
};
